import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Header from '../../../components/feature/Header';
import Footer from '../../home/components/Footer';
import { ShareModal } from '../../../components/base/ShareModal';
import ReportModal from '../../../components/base/ReportModal';
import { AuthModal } from '../../../components/feature/AuthModal';
import { mockDiscussions, discussionCategories } from '../../../mocks/discussions';
import { parseUrlParams, getIdForDatabase } from '../../../utils/urlUtils';
import { Link as RouterLink } from 'react-router-dom';
import ReputationLevelBadge from '../../../components/base/ReputationLevelBadge';

interface ForumPostDetailProps {
  categoryId: string;
}

interface MockComment {
  id: string;
  content: string;
  author: { username: string; avatar: string; reputation: number };
  createdAt: string;
  likes: number;
}

const categoryConfig: Record<string, { label: string; color: string; bgColor: string; accentBg: string; accentText: string }> = {
  general: { label: 'General Discussion', color: 'text-blue-600', bgColor: 'bg-blue-600', accentBg: 'bg-blue-50', accentText: 'text-blue-700' },
  reviews: { label: 'Drama Reviews', color: 'text-yellow-600', bgColor: 'bg-yellow-500', accentBg: 'bg-yellow-50', accentText: 'text-yellow-700' },
  creators: { label: 'Creator Spotlight', color: 'text-rose-600', bgColor: 'bg-rose-500', accentBg: 'bg-rose-50', accentText: 'text-rose-700' },
  recommendations: { label: 'Recommendations', color: 'text-green-600', bgColor: 'bg-green-500', accentBg: 'bg-green-50', accentText: 'text-green-700' },
  news: { label: 'Industry News', color: 'text-red-600', bgColor: 'bg-red-500', accentBg: 'bg-red-50', accentText: 'text-red-700' },
  'behind-scenes': { label: 'Behind the Scenes', color: 'text-teal-600', bgColor: 'bg-teal-500', accentBg: 'bg-teal-50', accentText: 'text-teal-700' },
};

const mockCommentsMap: Record<string, MockComment[]> = {
  'disc-001': [
    { id: 'c1-1', content: 'For me, it\'s all about the storytelling. A great Arab drama needs a compelling narrative that reflects real cultural experiences and emotions.', author: { username: 'Khalid Nasser', avatar: 'https://public.readdy.ai/ai/img_res/a6daab2a8c3cd1986795e23e279a456f.jpg', reputation: 245 }, createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), likes: 14 },
    { id: 'c1-2', content: 'Production quality has improved so much in recent years. The cinematography in shows like Desert Winds is on par with international productions.', author: { username: 'Nadia Salim', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Middle%20Eastern%20woman%20with%20bright%20smile%20wearing%20elegant%20blouse%20studio%20lighting%20clean%20background&width=80&height=80&seq=c1b&orientation=squarish', reputation: 312 }, createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(), likes: 9 },
  ],
  'disc-002': [
    { id: 'c2-1', content: 'Noor has been my absolute favorite this year. The emotional depth is unmatched!', author: { username: 'Yasmin Farah', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20enthusiastic%20expression%20wearing%20colorful%20scarf%20studio%20lighting%20clean%20background&width=80&height=80&seq=c2a&orientation=squarish', reputation: 198 }, createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), likes: 22 },
    { id: 'c2-2', content: 'Desert Winds for sure. The production value and acting are incredible. Can\'t wait for season 2!', author: { username: 'Tariq Mansour', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20excited%20expression%20wearing%20casual%20polo%20shirt%20natural%20lighting%20clean%20background&width=80&height=80&seq=c2b&orientation=squarish', reputation: 167 }, createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), likes: 18 },
  ],
  'disc-003': [
    { id: 'c3-1', content: 'That scene between Noor and her mother had me in tears. The acting was phenomenal and so authentic.', author: { username: 'Rania Khoury', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20woman%20with%20emotional%20expression%20wearing%20elegant%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c3a&orientation=squarish', reputation: 278 }, createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), likes: 31 },
    { id: 'c3-2', content: 'I agree with the 5-star rating. This episode elevated the entire series to a new level.', author: { username: 'Hassan Bakri', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20appreciative%20expression%20wearing%20casual%20shirt%20natural%20lighting%20clean%20background&width=80&height=80&seq=c3b&orientation=squarish', reputation: 203 }, createdAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(), likes: 15 },
  ],
  'disc-004': [
    { id: 'c4-1', content: 'The way they tied up all the storylines was masterful. Every character got a satisfying conclusion.', author: { username: 'Leila Hamdan', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20satisfied%20expression%20wearing%20modern%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c4a&orientation=squarish', reputation: 334 }, createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), likes: 27 },
    { id: 'c4-2', content: 'That final scene in the desert was breathtaking. The cinematography deserves every award.', author: { username: 'Faisal Al-Otaibi', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20impressed%20expression%20wearing%20business%20casual%20natural%20lighting%20clean%20background&width=80&height=80&seq=c4b&orientation=squarish', reputation: 189 }, createdAt: new Date(Date.now() - 3.5 * 60 * 60 * 1000).toISOString(), likes: 20 },
  ],
  'disc-005': [
    { id: 'c5-1', content: 'Her performance in Noor was a turning point. She went from promising newcomer to leading lady overnight.', author: { username: 'Dina Sabbagh', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20woman%20with%20admiring%20expression%20wearing%20professional%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c5a&orientation=squarish', reputation: 256 }, createdAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(), likes: 19 },
    { id: 'c5-2', content: 'What I love most is her versatility. She can do comedy, drama, and action equally well.', author: { username: 'Samir Haddad', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20thoughtful%20expression%20wearing%20smart%20casual%20attire%20natural%20lighting%20clean%20background&width=80&height=80&seq=c5b&orientation=squarish', reputation: 178 }, createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), likes: 12 },
  ],
  'disc-006': [
    { id: 'c6-1', content: 'The Merchant\'s Tale is absolutely incredible! I can\'t believe more people haven\'t seen it. Thank you for this list!', author: { username: 'Mariam Zayed', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20grateful%20expression%20wearing%20casual%20modern%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c6a&orientation=squarish', reputation: 289 }, createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(), likes: 24 },
    { id: 'c6-2', content: 'Coastal Dreams was my hidden gem discovery of the year. The soundtrack alone is worth watching for.', author: { username: 'Ayman Darwish', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20enthusiastic%20expression%20wearing%20casual%20attire%20natural%20lighting%20clean%20background&width=80&height=80&seq=c6b&orientation=squarish', reputation: 156 }, createdAt: new Date(Date.now() - 20 * 60 * 1000).toISOString(), likes: 16 },
    { id: 'c6-3', content: 'Adding Mountain Echoes to my watchlist right now. Your recommendations never disappoint!', author: { username: 'Hana Bishara', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Middle%20Eastern%20woman%20with%20excited%20expression%20wearing%20trendy%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c6c&orientation=squarish', reputation: 134 }, createdAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(), likes: 11 },
  ],
  'disc-007': [
    { id: 'c7-1', content: 'This is fantastic news for the industry! Dubai is becoming a real hub for Arab entertainment production.', author: { username: 'Walid Kassem', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20businessman%20with%20optimistic%20expression%20wearing%20suit%20natural%20lighting%20clean%20background&width=80&height=80&seq=c7a&orientation=squarish', reputation: 345 }, createdAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(), likes: 18 },
    { id: 'c7-2', content: 'I hope this leads to more diverse storytelling and opportunities for emerging creators in the region.', author: { username: 'Lina Tabbara', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20woman%20with%20hopeful%20expression%20wearing%20business%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c7b&orientation=squarish', reputation: 267 }, createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), likes: 14 },
  ],
  'disc-008': [
    { id: 'c8-1', content: 'The dedication of the crew filming in 45°C heat is incredible. Those desert scenes were absolutely worth it.', author: { username: 'Rami Nassar', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20impressed%20expression%20wearing%20casual%20desert%20attire%20natural%20lighting%20clean%20background&width=80&height=80&seq=c8a&orientation=squarish', reputation: 223 }, createdAt: new Date(Date.now() - 2.5 * 60 * 60 * 1000).toISOString(), likes: 25 },
    { id: 'c8-2', content: 'As someone who works in film production, I can appreciate how challenging desert filming is. The results speak for themselves.', author: { username: 'Joud Al-Harbi', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20film%20professional%20with%20knowledgeable%20expression%20wearing%20crew%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c8b&orientation=squarish', reputation: 412 }, createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), likes: 19 },
  ],
  'disc-009': [
    { id: 'c9-1', content: 'The dialogue writing in this episode was particularly sharp. Every conversation felt natural and meaningful.', author: { username: 'Nour Issa', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Middle%20Eastern%20woman%20with%20analytical%20expression%20wearing%20glasses%20studio%20lighting%20clean%20background&width=80&height=80&seq=c9a&orientation=squarish', reputation: 298 }, createdAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(), likes: 13 },
  ],
  'disc-010': [
    { id: 'c10-1', content: 'Ahmed Hassan\'s attention to detail is what sets him apart. Every frame in Desert Winds feels intentional.', author: { username: 'Zain Mourad', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20admiring%20expression%20wearing%20modern%20attire%20natural%20lighting%20clean%20background&width=80&height=80&seq=c10a&orientation=squarish', reputation: 267 }, createdAt: new Date(Date.now() - 3.5 * 60 * 60 * 1000).toISOString(), likes: 21 },
    { id: 'c10-2', content: 'I watched that interview too! His creative process is fascinating. He storyboards every single scene himself.', author: { username: 'Salma Qasim', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20interested%20expression%20wearing%20casual%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c10b&orientation=squarish', reputation: 189 }, createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), likes: 16 },
  ],
  'disc-011': [
    { id: 'c11-1', content: '$50 million is a serious commitment. This could be a game-changer for original Arab content on streaming platforms.', author: { username: 'Bassam Fares', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20businessman%20with%20analytical%20expression%20wearing%20formal%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c11a&orientation=squarish', reputation: 378 }, createdAt: new Date(Date.now() - 3.5 * 60 * 60 * 1000).toISOString(), likes: 28 },
    { id: 'c11-2', content: 'Finally! Arab stories deserve the same production budgets as Western content. This is long overdue.', author: { username: 'Maya Shalhoub', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20woman%20with%20passionate%20expression%20wearing%20professional%20attire%20natural%20lighting%20clean%20background&width=80&height=80&seq=c11b&orientation=squarish', reputation: 234 }, createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), likes: 22 },
  ],
  'disc-012': [
    { id: 'c12-1', content: 'The costumes in The Last Garden were stunning. You could tell every piece was researched and crafted with care.', author: { username: 'Rana Habib', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20woman%20with%20appreciative%20expression%20wearing%20artistic%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=c12a&orientation=squarish', reputation: 312 }, createdAt: new Date(Date.now() - 4.5 * 60 * 60 * 1000).toISOString(), likes: 17 },
    { id: 'c12-2', content: 'Amira Khalil is a true artist. The way she blends historical accuracy with cinematic appeal is remarkable.', author: { username: 'Karim Awad', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20respectful%20expression%20wearing%20smart%20casual%20attire%20natural%20lighting%20clean%20background&width=80&height=80&seq=c12b&orientation=squarish', reputation: 198 }, createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), likes: 14 },
  ],
};

export default function ForumPostDetail({ categoryId }: ForumPostDetailProps) {
  const { urlParam } = useParams<{ urlParam: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState<typeof mockDiscussions[0] | null>(null);
  const [comments, setComments] = useState<MockComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const [localUpvotes, setLocalUpvotes] = useState(0);
  const [localDownvotes, setLocalDownvotes] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const config = categoryConfig[categoryId] || categoryConfig.general;

  useEffect(() => {
    if (!urlParam) {
      navigate(`/forum`);
      return;
    }

    // Try to find the post by matching the urlParam against mock data
    // The URL format is: disc-001-what-makes-great-arab-drama-storytelling-elements
    // We need to extract the ID (disc-001) from the slug
    const found = mockDiscussions.find((d) => {
      if (d.category !== categoryId) return false;
      // Check if urlParam starts with the post id
      if (urlParam === d.id || urlParam === d.slug) return true;
      if (urlParam.startsWith(d.id + '-')) return true;
      if (urlParam.startsWith(d.slug)) return true;
      return false;
    });

    if (found) {
      setPost(found);
      setLocalUpvotes(found.upvotes);
      setLocalDownvotes(found.downvotes);
      setComments(mockCommentsMap[found.id] || []);
      setLoading(false);
      document.title = `${found.title} - ${config.label} | DramaHub Forum`;
    } else {
      // Try Supabase for real posts
      let postId = urlParam;
      try {
        const { id } = parseUrlParams(urlParam);
        postId = id;
      } catch {
        // Use urlParam as-is
      }
      fetchFromSupabase(postId);
    }
  }, [urlParam, categoryId]);

  const fetchFromSupabase = async (id: string) => {
    try {
      let dbId = id;
      try { dbId = getIdForDatabase(id); } catch { /* use as-is */ }

      const { data, error } = await supabase
        .from('posts')
        .select(`*, user_profiles(username, avatar_url, reputation), discussion_categories(name, color)`)
        .eq('id', dbId)
        .maybeSingle();

      if (error || !data) {
        setLoading(false);
        return;
      }

      const mapped = {
        id: data.id,
        slug: '',
        title: data.title,
        content: data.content || '',
        category: categoryId,
        categoryName: config.label,
        author: {
          id: data.author_id,
          username: data.user_profiles?.username || 'Unknown',
          avatar: data.user_profiles?.avatar_url || '',
          reputation: data.user_profiles?.reputation || 0,
        },
        upvotes: data.upvotes || 0,
        downvotes: data.downvotes || 0,
        commentCount: 0,
        views: 0,
        tags: data.tags || [],
        isPinned: false,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
      setPost(mapped as any);
      setLocalUpvotes(mapped.upvotes);
      setLocalDownvotes(mapped.downvotes);
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  };

  const handleVote = (type: 'up' | 'down') => {
    if (!user) { setShowAuthModal(true); return; }
    if (userVote === type) {
      setUserVote(null);
      if (type === 'up') setLocalUpvotes((v) => v - 1);
      else setLocalDownvotes((v) => v - 1);
    } else {
      if (userVote === 'up') setLocalUpvotes((v) => v - 1);
      if (userVote === 'down') setLocalDownvotes((v) => v - 1);
      setUserVote(type);
      if (type === 'up') setLocalUpvotes((v) => v + 1);
      else setLocalDownvotes((v) => v + 1);
    }
  };

  const handleAddComment = () => {
    if (!user) { setShowAuthModal(true); return; }
    if (!newComment.trim()) return;
    const c: MockComment = {
      id: `new-${Date.now()}`,
      content: newComment.trim(),
      author: { username: user.email?.split('@')[0] || 'You', avatar: '', reputation: 0 },
      createdAt: new Date().toISOString(),
      likes: 0,
    };
    setComments((prev) => [c, ...prev]);
    setNewComment('');
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderStars = (rating: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <i key={i} className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}></i>
    ));

  const handleBackToForum = () => {
    navigate(`/forum?tab=${categoryId}`);
  };

  const relatedPosts = post
    ? mockDiscussions
        .filter((d) => d.category === categoryId && d.id !== post.id)
        .slice(0, 5)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="bg-white rounded-lg p-6 space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
            <i className="ri-file-unknow-line text-3xl text-gray-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h1>
          <p className="text-gray-500 mb-6">This post doesn&apos;t exist or has been removed.</p>
          <button
            onClick={handleBackToForum}
            className={`inline-flex items-center px-5 py-2.5 ${config.bgColor} text-white rounded-lg hover:opacity-90 transition-colors cursor-pointer whitespace-nowrap`}
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to {config.label}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const netVotes = localUpvotes - localDownvotes;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button + Breadcrumb */}
        <div className="flex items-center justify-between mb-6">
          <nav className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-gray-700 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/forum" className="hover:text-gray-700 transition-colors">Forum</Link>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={handleBackToForum} className={`hover:${config.color} transition-colors cursor-pointer`}>
              {config.label}
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>

        {/* Back Button */}
        <button
          onClick={handleBackToForum}
          className={`inline-flex items-center px-4 py-2 mb-6 text-sm font-medium ${config.accentText} ${config.accentBg} rounded-lg hover:opacity-80 transition-colors cursor-pointer whitespace-nowrap`}
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back to {config.label}
        </button>

        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Main Post Card */}
            <article className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
              {/* Category Banner */}
              <div className={`${config.bgColor} px-6 py-3 flex items-center justify-between`}>
                <div className="flex items-center space-x-3 text-white">
                  <i className={`${discussionCategories.find((c) => c.id === categoryId)?.icon || 'ri-chat-3-line'}`}></i>
                  <span className="font-medium text-sm">{config.label}</span>
                </div>
                <span className="text-white/80 text-sm">{formatTimeAgo(post.createdAt)}</span>
              </div>

              <div className="p-6">
                {/* Badges */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {(post as any).isBreaking && (
                    <span className="inline-flex items-center px-2.5 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                      <i className="ri-alarm-warning-line mr-1"></i>Breaking
                    </span>
                  )}
                  {post.isPinned && (
                    <span className="inline-flex items-center px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      <i className="ri-pushpin-fill mr-1"></i>Pinned
                    </span>
                  )}
                  {(post as any).source && (
                    <span className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      Source: {(post as any).source}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">{post.title}</h1>

                {/* Author */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <RouterLink to={`/user/${post.author.id}`} className="cursor-pointer">
                      <img
                        src={post.author.avatar}
                        alt={post.author.username}
                        className="w-11 h-11 rounded-full object-cover border-2 border-gray-100 hover:border-amber-300 transition-colors"
                      />
                    </RouterLink>
                    <div>
                      <RouterLink 
                        to={`/user/${post.author.id}`}
                        className="font-semibold text-gray-900 text-sm hover:text-amber-600 transition-colors cursor-pointer"
                      >
                        {post.author.username}
                      </RouterLink>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <ReputationLevelBadge points={post.author.reputation} size="xs" />
                        <span>•</span>
                        <span>{formatTimeAgo(post.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Menu */}
                  <div className="relative">
                    <button onClick={() => setShowMenu(!showMenu)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                      <i className="ri-more-2-fill text-lg"></i>
                    </button>
                    {showMenu && (
                      <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <button onClick={() => { setShowShareModal(true); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer">
                          <i className="ri-share-line mr-2"></i>Share
                        </button>
                        <button onClick={() => { setBookmarked(!bookmarked); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer">
                          <i className={`${bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} mr-2`}></i>{bookmarked ? 'Unsave' : 'Save'}
                        </button>
                        <button onClick={() => { setShowReportModal(true); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center cursor-pointer">
                          <i className="ri-flag-line mr-2"></i>Report
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Review-specific: Rating & Drama */}
                {((post as any).rating || (post as any).drama) && (
                  <div className={`${config.accentBg} rounded-lg p-4 mb-6`}>
                    <div className="flex items-center flex-wrap gap-4">
                      {(post as any).drama && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">
                            {categoryId === 'reviews' ? 'Reviewing:' : 'Related Drama:'}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">{(post as any).drama}</span>
                        </div>
                      )}
                      {(post as any).rating && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">Rating:</span>
                          <div className="flex items-center space-x-1">
                            {renderStars((post as any).rating)}
                            <span className="text-sm text-gray-600 ml-1">({(post as any).rating}/5)</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Recommended Dramas */}
                {(post as any).recommendedDramas && (
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-semibold text-green-800 mb-3">
                      <i className="ri-film-line mr-1"></i>Recommended Dramas
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(post as any).recommendedDramas.map((drama: string, i: number) => (
                        <span key={i} className="px-3 py-1.5 bg-white text-green-700 text-sm rounded-lg border border-green-200 font-medium">
                          {drama}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="prose max-w-none mb-6">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-[15px]">{post.content}</div>
                </div>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {post.tags.map((tag, i) => (
                      <span key={i} className={`px-3 py-1 ${config.accentBg} ${config.accentText} text-xs rounded-full font-medium`}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Stats & Actions */}
                <div className="flex items-center justify-between pt-5 border-t border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden">
                      <button
                        onClick={() => handleVote('up')}
                        className={`flex items-center space-x-1 px-3 py-2 text-sm transition-colors cursor-pointer ${
                          userVote === 'up' ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        <i className="ri-arrow-up-line"></i>
                        <span className="font-medium">{localUpvotes}</span>
                      </button>
                      <div className="w-px h-6 bg-gray-200"></div>
                      <button
                        onClick={() => handleVote('down')}
                        className={`flex items-center space-x-1 px-3 py-2 text-sm transition-colors cursor-pointer ${
                          userVote === 'down' ? 'bg-red-100 text-red-700' : 'text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        <i className="ri-arrow-down-line"></i>
                        <span className="font-medium">{localDownvotes}</span>
                      </button>
                    </div>

                    <span className={`text-sm font-semibold ${netVotes > 0 ? 'text-green-600' : netVotes < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                      {netVotes > 0 ? '+' : ''}{netVotes} score
                    </span>

                    <span className="flex items-center text-sm text-gray-500">
                      <i className="ri-eye-line mr-1"></i>{post.views.toLocaleString()}
                    </span>
                    <span className="flex items-center text-sm text-gray-500">
                      <i className="ri-message-3-line mr-1"></i>{comments.length}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button onClick={() => setBookmarked(!bookmarked)} className={`flex items-center space-x-1 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${bookmarked ? `${config.accentBg} ${config.accentText}` : 'text-gray-500 hover:bg-gray-100'}`}>
                      <i className={bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                      <span className="hidden sm:inline">{bookmarked ? 'Saved' : 'Save'}</span>
                    </button>
                    <button onClick={() => setShowShareModal(true)} className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-share-line"></i>
                      <span className="hidden sm:inline">Share</span>
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Comments Section */}
            <section className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">
                  <i className="ri-chat-3-line mr-2"></i>
                  Comments ({comments.length})
                </h2>
              </div>

              {/* Comment Form */}
              <div className="p-6 border-b border-gray-100">
                {user ? (
                  <div className="flex items-start space-x-3">
                    <div className={`w-9 h-9 ${config.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-sm font-medium">{user.email?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Share your thoughts..."
                        rows={3}
                        maxLength={500}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-300 focus:border-gray-300 resize-none text-sm"
                      />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">{newComment.length}/500</span>
                        <button
                          onClick={handleAddComment}
                          disabled={!newComment.trim()}
                          className={`px-4 py-2 ${config.bgColor} text-white text-sm rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap`}
                        >
                          Post Comment
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-sm mb-3">Sign in to join the discussion</p>
                    <button onClick={() => setShowAuthModal(true)} className={`px-5 py-2 ${config.bgColor} text-white text-sm rounded-lg hover:opacity-90 transition-colors cursor-pointer whitespace-nowrap`}>
                      Sign In
                    </button>
                  </div>
                )}
              </div>

              {/* Comments List */}
              <div className="divide-y divide-gray-50">
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className="p-6 hover:bg-gray-50/50 transition-colors">
                      <div className="flex items-start space-x-3">
                        {comment.author.avatar ? (
                          <img src={comment.author.avatar} alt={comment.author.username} className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-medium">{comment.author.username.charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1.5">
                            <span className="font-medium text-gray-900 text-sm hover:text-amber-600 transition-colors cursor-pointer">{comment.author.username}</span>
                            {comment.author.reputation > 0 && (
                              <ReputationLevelBadge points={comment.author.reputation} size="xs" />
                            )}
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-400">{formatTimeAgo(comment.createdAt)}</span>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed mb-2">{comment.content}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-400">
                            <button className="flex items-center space-x-1 hover:text-red-500 transition-colors cursor-pointer">
                              <i className="ri-heart-line"></i>
                              <span>{comment.likes}</span>
                            </button>
                            <button className="hover:text-gray-600 transition-colors cursor-pointer">Reply</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <i className="ri-chat-3-line text-4xl text-gray-200 mb-3"></i>
                    <p className="text-gray-400 text-sm">No comments yet. Be the first to share your thoughts!</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Related Posts Sidebar */}
          {relatedPosts.length > 0 && (
            <aside className="w-80 flex-shrink-0 hidden lg:block">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-24">
                <div className={`${config.bgColor} px-5 py-3 rounded-t-lg`}>
                  <h3 className="text-white font-semibold text-sm flex items-center">
                    <i className="ri-article-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Related Posts
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {relatedPosts.map((rp) => {
                    const slug = rp.slug || rp.id;
                    const url = `/forum/${categoryId}/${rp.id}-${slug}`;
                    return (
                      <RouterLink
                        key={rp.id}
                        to={url}
                        className="block px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer group"
                      >
                        <h4 className="text-sm font-medium text-gray-900 group-hover:text-gray-700 leading-snug line-clamp-2 mb-2">
                          {rp.title}
                        </h4>
                        <div className="flex items-center justify-between">
                          <RouterLink 
                            to={`/user/${rp.author.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={rp.author.avatar}
                              alt={rp.author.username}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                            <span className="text-xs text-gray-500 truncate max-w-[100px] hover:text-amber-600 transition-colors">{rp.author.username}</span>
                          </RouterLink>
                          <div className="flex items-center space-x-3 text-xs text-gray-400">
                            <span className="flex items-center">
                              <i className="ri-arrow-up-line mr-0.5"></i>{rp.upvotes}
                            </span>
                            <span className="flex items-center">
                              <i className="ri-message-3-line mr-0.5"></i>{rp.commentCount}
                            </span>
                          </div>
                        </div>
                      </RouterLink>
                    );
                  })}
                </div>
                <div className="px-5 py-3 border-t border-gray-100">
                  <button
                    onClick={handleBackToForum}
                    className={`w-full text-center text-sm font-medium ${config.accentText} hover:underline cursor-pointer whitespace-nowrap`}
                  >
                    View all in {config.label} →
                  </button>
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      <Footer />

      {/* Modals */}
      {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={post.title}
          url={window.location.href}
        />
      )}

      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="post"
          contentId={post.id}
          reportedUserId={post.author.id}
        />
      )}

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode="signin"
        />
      )}
    </div>
  );
}
